import React, { useState, useContext } from 'react';
import notifyHB from '@/config/notifyHB';
import { toast } from '@/components/toast/toast';
import { useHistory } from 'react-router';
import ComponentContainer from '../../components/component-container/ComponentContainer';
import { AuthContext } from '../../components/auth-context/auth-context';
import routeConfig from '../../config/routeConfig';
import OtpConfirmation, { OtpConfirmationProps } from './otpConfirmation';
import { signup } from '../../use-cases/signup';
import { login } from '../../use-cases/login';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2Icon } from 'lucide-react';
import styles from './login-styles.module.css';
import { LoginWithOtpResponse } from '@/use-cases/login-with-otp';
import { getUserCertificates } from '@/use-cases/get-user-signatures';
import { getErrorReason, trackEvent } from '@/utils/analytics';

const REQUIRED_LOGIN_FIELDS = ['session', 'method', 'email', 'user_id'];
const hasRequiredFields = (data: Record<string, unknown>): boolean => {
	return REQUIRED_LOGIN_FIELDS.every((field) => !!data?.[field]);
};

function Login() {
	const [isLoading, setIsLoading] = useState(false);
	const [email, setEmail] = useState('');
	const [sessionId, setSessionId] = useState('');
	const [userId, setUserId] = useState('');
	const [showOtpForm, setShowOtpForm] = useState(false);
	const history = useHistory();
	const authCtx = useContext(AuthContext);

	const handleSignupAndLogin = async (event: React.FormEvent) => {
		event.preventDefault();

		let failedCall = 'signup';
		try {
			setIsLoading(true);
			const signupResponse = await signup(email);

			if (
				signupResponse.status === 200 ||
				signupResponse.status === 201
			) {
				failedCall = 'login';
				const loginResponse = await login(email);

				if (hasRequiredFields(loginResponse)) {
					setSessionId(loginResponse.session);
					setUserId(loginResponse.user_id);
					localStorage.setItem('user-email', loginResponse.email);
					localStorage.setItem('user-id', loginResponse.user_id);
					setShowOtpForm(true);
					setIsLoading(false);
				} else {
					toast({ mode: 'destructive', description: 'Error al iniciar sesión' });
					trackEvent('onboarding_error', {
						step: 'login',
						reason: 'login_failed',
					});
					setIsLoading(false);
				}
			} else {
				toast({ mode: 'destructive', description: 'El registro no fue exitoso.' });
				trackEvent('onboarding_error', {
					step: 'login',
					reason: 'signup_failed',
				});
				setIsLoading(false);
			}
		} catch (error) {
			toast({ mode: 'destructive', description: 'Ocurrió un error inesperado. Intenta de nuevo más tarde' });
			trackEvent('onboarding_error', {
				step: 'login',
				reason: `${failedCall}_${getErrorReason(error)}`,
			});
			notifyHB('login', 'Login', error);
			setIsLoading(false);
		}
	};

	const setUpSessionToken = (accessResponse: LoginWithOtpResponse) => {
		authCtx.saveToken(accessResponse);
	};

	const redirectSession = async (onboardingUlid: string) => {
		try {
			const certificates = await getUserCertificates();
			if (certificates.length === 0) {
				// The wizard reads the step from the onboarding status endpoint.
				if (onboardingUlid) {
					localStorage.setItem('onboarding-ulid', onboardingUlid);
				}
				history.push(routeConfig.requestSignature);
			} else {
				history.push(routeConfig.dashboard);
			}
		} catch (error) {
			history.push(routeConfig.dashboard);
		}
	};

	const handleOtpSuccess: OtpConfirmationProps['onSuccess'] = async (
		loginData,
	) => {
		const onboardingUlid = loginData.onboarding_process_ulid;
		setUpSessionToken(loginData);
		await redirectSession(onboardingUlid);
	};

	const handleOtpCancel = () => {
		setShowOtpForm(false);
	};

	return (
		<ComponentContainer customBackground>
			{!showOtpForm ? (
				<form className={styles.login} onSubmit={handleSignupAndLogin}>
					<div className={styles.title}>Inicia sesión</div>
					<div className={styles['email-input']}>
						<Label htmlFor="email">Correo electrónico</Label>
						<Input
							type="email"
							id="email"
							placeholder="email@example.com"
							autoFocus
							value={email}
							required
							disabled={isLoading}
							onChange={(e) => setEmail(e.target.value)}
						/>
					</div>
					<div className={styles['login-button']}>
						<Button
							variant="default"
							type="submit"
							disabled={isLoading}
						>
							{isLoading && (
								<Loader2Icon className="animate-spin" />
							)}
							Ingresar
						</Button>
						<Label className={styles['label']}>
							¿No tienes una cuenta de Cybersign? Descuida,
							ingresa tu correo y te enviaremos un código de
							acceso.
						</Label>
					</div>
				</form>
			) : (
				<OtpConfirmation
					email={email}
					sessionId={sessionId}
					userId={userId}
					onSuccess={handleOtpSuccess}
					onCancel={handleOtpCancel}
				/>
			)}
		</ComponentContainer>
	);
}

export default Login;
